import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import styled, { keyframes, css } from 'styled-components';
import { FaUser, FaTrophy, FaCalendar, FaCog, FaKey, FaBook, FaEdit, FaSave, FaTimes, FaShieldAlt, FaGamepad, FaStar, FaQrcode, FaGift, FaUsers, FaMobileAlt, FaApple, FaAndroid, FaShareAlt, FaPlus, FaEllipsisV, FaHome, FaCamera, FaPalette, FaCheck } from 'react-icons/fa';
import { theme } from '../../styles/theme';
import { useAuth } from '../../hooks/useApi';
import ApiService from '../../services/ApiService';
import PWAInstallBanner from '../PWA/PWAInstallBanner';

const MEDIA_BASE_URL = (
  process.env.REACT_APP_BACKEND_URL ||
  process.env.REACT_APP_API_URL ||
  ''
).replace(/\/$/, '');

const resolveAvatarUrl = (url) => {
  if (!url) return null;
  const s = String(url).trim();
  if (!s) return null;
  if (s.startsWith('http://') || s.startsWith('https://') || s.startsWith('data:') || s.startsWith('blob:')) return s;
  if (!MEDIA_BASE_URL) return s;
  return `${MEDIA_BASE_URL}${s.startsWith('/') ? '' : '/'}${s}`;
};

const fadeIn = theme.animations?.fadeIn || `
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const sparkle = keyframes`
  0%, 100% {
    opacity: 0.3;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1);
  }
`;
const ProfileContainer = styled.div`
  min-height: 100vh;
  background: black;
  padding: ${theme.spacing.xl};
  position: relative;
  overflow: hidden;
  overflow-x: hidden;
  box-sizing: border-box;

 &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: radial-gradient(circle at 20% 80%, rgba(0, 0, 0, 0.1) 0%, transparent 50%),
                      radial-gradient(circle at 80% 20%, rgba(0, 0, 0, 0.1) 0%, transparent 50%),
                      radial-gradient(circle at 40% 40%, rgba(0, 0, 0, 0.1) 0%, transparent 50%);
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: radial-gradient(circle at 2% 4%, rgba(255, 255, 255, 0.26) 1px, transparent 0),
                      radial-gradient(circle at 29% 36%, rgba(255, 255, 255, 0.32) 1px, transparent 0),
                      radial-gradient(circle at 50% 60%, rgba(255, 255, 255, 0.2) 1px, transparent 0),
                      radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.66) 1px, transparent 0),
                      radial-gradient(circle at 70% 10%, rgba(255, 255, 255, 0.11) 1px, transparent 0),
                      radial-gradient(circle at 60% 12%, rgba(255, 255, 255, 0.29) 1px, transparent 0),
                      radial-gradient(circle at 43% 90%, rgba(255, 255, 255, 0.51) 1px, transparent 0),
                      radial-gradient(circle at 32% 83%, rgba(255, 255, 255, 0.16) 1px, transparent 0),
                      radial-gradient(circle at 49% 53%, rgba(255, 255, 255, 0.6) 1px, transparent 0),
                      radial-gradient(circle at 89% 52%, rgba(255, 255, 255, 0.14) 1px, transparent 0);

    
    background-size: 25% 25%;
    pointer-events: none;
    background-repeat: repeat;
    animation: ${sparkle} 4s ease-in-out infinite;

  }
    
  @keyframes sparkle {
    0%, 100% { opacity: 0.5; }
    50% { opacity: 1; }
  }

  @media (max-width: 768px) {
    padding: ${theme.spacing.lg};
  }

  @media (max-width: 480px) {
    padding: ${theme.spacing.md};
  }
`;

const ContentWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xl};
  position: relative;
  z-index: 1;
  animation: ${fadeIn} 0.6s ease-out;
`;

const ProfileHeader = styled.div`
  background: ${theme.colors.cardBackground};
  border: 1px solid ${theme.colors.cardBorder};
  border-radius: ${theme.borderRadius.large};
  padding: ${theme.spacing.xxl};
  box-shadow: ${theme.shadows.card};
  backdrop-filter: blur(10px);
  position: relative;
  overflow: hidden;
  width: 100%;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 20% 80%, rgba(0, 215, 255, 0.1) 0%, transparent 50%),
                radial-gradient(circle at 80% 20%, rgba(0, 140, 255, 0.1) 0%, transparent 50%);
    pointer-events: none;
  }

  @media (max-width: 768px) {
    padding: ${theme.spacing.xl};
  }

  @media (max-width: 480px) {
    padding: ${theme.spacing.lg};
  }
`;

const HeaderContent = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xl};

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

const AvatarSection = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const Avatar = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: ${theme.gradients.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 3rem;
  font-weight: ${theme.typography.fontWeights.bold};
  color: #000;
  box-shadow: 0 8px 32px ${props => props.frameColor ? `${props.frameColor}66` : 'rgba(255, 215, 0, 0.4)'};
  border: 4px solid ${props => props.frameColor || 'rgba(255, 215, 0, 0.3)'};
  transition: border-color 0.3s ease, box-shadow 0.3s ease;

  @media (max-width: 480px) {
    width: 100px;
    height: 100px;
    font-size: 2.5rem;
  }
`;

const GamerBadge = styled.div`
  position: absolute;
  bottom: -5px;
  right: -5px;
  background: ${theme.gradients.button};
  border: 3px solid ${theme.colors.cardBackground};
  border-radius: 50%;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  box-shadow: ${theme.shadows.button};

  @media (max-width: 480px) {
    width: 40px;
    height: 40px;
    font-size: 1.25rem;
  }
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
`;

const AvatarOverlay = styled.button`
  position: absolute;
  top: 0;
  left: 0;
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.5);
  border: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  color: #fff;
  font-size: 0.65rem;
  opacity: 0;
  cursor: pointer;
  transition: opacity 0.2s ease;
  z-index: 2;

  ${AvatarSection}:hover & {
    opacity: 1;
  }

  svg {
    font-size: 1.2rem;
  }

  @media (max-width: 480px) {
    width: 100px;
    height: 100px;
  }
`;

const CustomizeBtn = styled.button`
  position: absolute;
  top: -4px;
  right: -4px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(12,12,22,0.92);
  border: 1px solid rgba(255,255,255,0.18);
  color: ${theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.72rem;
  cursor: pointer;
  z-index: 3;
  transition: all 0.2s;
  &:hover { background: rgba(255,215,0,0.2); transform: scale(1.1); }
`;

const modalSlideUp = keyframes`
  from { opacity: 0; transform: translate(-50%, -46%); }
  to   { opacity: 1; transform: translate(-50%, -50%); }
`;

const CosmeticsOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.82);
  backdrop-filter: blur(6px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CosmeticsModal = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 94%;
  max-width: 400px;
  max-height: 88vh;
  overflow-y: auto;
  background: linear-gradient(165deg, rgba(16,16,30,0.98), rgba(8,8,18,0.99));
  border: 1px solid rgba(255,215,0,0.12);
  border-radius: 20px;
  padding: 0;
  z-index: 10000;
  box-shadow: 0 24px 80px rgba(0,0,0,0.7), 0 0 40px rgba(255,215,0,0.06);
  animation: ${modalSlideUp} 0.28s ease-out;

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 3px; }
`;

const CosmeticsHeader = styled.div`
  padding: 20px 24px 16px;
  text-align: center;
  border-bottom: 1px solid rgba(255,255,255,0.06);
`;

const CosmeticsTitle = styled.h3`
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  background: ${theme.gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const CosmeticsSubtitle = styled.p`
  margin: 4px 0 0;
  font-size: 0.78rem;
  color: ${theme.colors.textMuted};
`;

const PreviewArea = styled.div`
  display: flex;
  justify-content: center;
  padding: 24px 0 20px;
  position: relative;
`;

const PreviewAvatar = styled.div`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: ${theme.gradients.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.2rem;
  font-weight: bold;
  color: #000;
  border: 4px solid ${props => props.frameColor || 'rgba(255,215,0,0.3)'};
  box-shadow: 0 0 24px ${props => props.frameColor ? `${props.frameColor}55` : 'rgba(255,215,0,0.25)'};
  transition: all 0.3s ease;
  position: relative;
`;

const PreviewAvatarImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
`;

const PreviewBadge = styled.div`
  position: absolute;
  bottom: -4px;
  right: -4px;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: ${theme.gradients.primary};
  border: 3px solid rgba(16,16,30,0.98);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  img { width: 100%; height: 100%; object-fit: contain; border-radius: 50%; }
`;

const CosmeticsBody = styled.div`
  padding: 0 24px 20px;
`;

const SectionLabel = styled.p`
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  color: ${theme.colors.textMuted};
  margin: 0 0 10px;
  font-weight: 600;
`;

const SwatchRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
  margin-bottom: 20px;
`;

const Swatch = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${props => props.color};
  border: 3px solid ${props => props.active ? '#fff' : 'rgba(255,255,255,0.08)'};
  cursor: pointer;
  transition: transform 0.18s, border-color 0.18s, box-shadow 0.18s;
  box-shadow: 0 0 ${props => props.active ? '14px' : '8px'} ${props => props.color}${props => props.active ? 'cc' : '66'};
  &:hover { transform: scale(1.15); border-color: rgba(255,255,255,0.6); }
`;

const NoneBtn = styled.button`
  height: 36px;
  padding: 0 14px;
  border-radius: 18px;
  background: ${props => props.active ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.04)'};
  border: 1px solid ${props => props.active ? 'rgba(255,255,255,0.35)' : 'rgba(255,255,255,0.1)'};
  color: ${props => props.active ? '#fff' : theme.colors.textMuted};
  font-size: 0.78rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  &:hover { background: rgba(255,255,255,0.12); }
`;

const BadgeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(58px, 1fr));
  gap: 10px;
  margin-bottom: 20px;
`;

const glowPulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 rgba(255,215,0,0); }
  50% { box-shadow: 0 0 12px rgba(255,215,0,0.35); }
`;

const BadgeOption = styled.button`
  width: 58px;
  height: 58px;
  border-radius: 12px;
  overflow: hidden;
  border: 2px solid ${props => props.active ? theme.colors.primary : 'rgba(255,255,255,0.08)'};
  background: ${props => props.active ? 'rgba(255,215,0,0.08)' : 'rgba(255,255,255,0.03)'};
  cursor: pointer;
  padding: 4px;
  transition: border-color 0.18s, transform 0.18s, background 0.18s;
  ${props => props.active && css`animation: ${glowPulse} 2s ease-in-out infinite;`}
  &:hover { transform: scale(1.08); border-color: rgba(255,215,0,0.5); background: rgba(255,215,0,0.06); }
  img { width: 100%; height: 100%; object-fit: contain; }
`;

const BadgeOptionLabel = styled.span`
  display: block;
  font-size: 0.55rem;
  color: ${theme.colors.textMuted};
  text-align: center;
  margin-top: 2px;
  line-height: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CosmeticsFooter = styled.div`
  display: flex;
  gap: 10px;
  padding: 0 24px 20px;
`;

const ApplyBtn = styled.button`
  flex: 1;
  padding: 12px;
  background: ${theme.gradients.primary};
  border: none;
  border-radius: 12px;
  color: #000;
  font-weight: 700;
  font-size: 0.9rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: transform 0.15s, box-shadow 0.15s;
  &:hover { transform: translateY(-1px); box-shadow: 0 4px 16px rgba(255,215,0,0.3); }
  &:disabled { opacity: 0.6; cursor: not-allowed; transform: none; box-shadow: none; }
`;

const CancelBtn = styled.button`
  padding: 12px 20px;
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 12px;
  color: ${theme.colors.textSecondary};
  font-weight: 500;
  font-size: 0.9rem;
  cursor: pointer;
  transition: background 0.15s;
  &:hover { background: rgba(255,255,255,0.1); }
`;

const ProfileInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;

const ProfileName = styled.h1`
  font-size: clamp(1.75rem, 4vw, 2.5rem);
  font-weight: ${theme.typography.fontWeights.bold};
  background: ${theme.gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const ProfileEmail = styled.p`
  color: ${theme.colors.textSecondary};
  margin: 0;
  font-size: ${theme.typography.fontSizes.md};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const ProfileStats = styled.div`
  display: flex;
  gap: ${theme.spacing.lg};
  margin-top: ${theme.spacing.md};

  @media (max-width: 768px) {
    justify-content: center;
    flex-wrap: wrap;
  }
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  color: ${theme.colors.textMuted};
  font-size: ${theme.typography.fontSizes.sm};

  svg {
    color: ${theme.colors.primary};
  }
`;

const SettingsSection = styled.div`
  background: ${theme.colors.cardBackground};
  border: 1px solid ${theme.colors.cardBorder};
  border-radius: ${theme.borderRadius.large};
  padding: ${theme.spacing.xl};
  box-shadow: ${theme.shadows.card};
  backdrop-filter: blur(10px);
  width: 100%;

  @media (max-width: 768px) {
    padding: ${theme.spacing.lg};
  }

  @media (max-width: 480px) {
    padding: ${theme.spacing.md};
  }
`;

const SectionTitle = styled.h2`
  font-size: ${theme.typography.fontSizes.xxl};
  font-weight: ${theme.typography.fontWeights.bold};
  background: ${theme.gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0 0 ${theme.spacing.xl} 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

const SettingsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${theme.spacing.lg};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SettingCard = styled.div`
  background: rgba(20, 20, 35, 0);
  backdrop-filter: blur(10px);
  border: 1px solid ${theme.colors.backgroundLight};
  border-radius: ${theme.borderRadius.medium};
  padding: ${theme.spacing.lg};
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    border-color: ${theme.colors.primary};
    box-shadow: 0 8px 24px rgba(255, 215, 0, 0.2);
  }

  @media (max-width: 480px) {
    padding: ${theme.spacing.md};
  }
`;

const SettingIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: ${theme.borderRadius.medium};
  background: ${theme.gradients.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.75rem;
  color: #000;
  margin-bottom: ${theme.spacing.md};
`;

const SettingTitle = styled.h3`
  font-size: ${theme.typography.fontSizes.lg};
  font-weight: ${theme.typography.fontWeights.semibold};
  color: ${theme.colors.textPrimary};
  margin: 0 0 ${theme.spacing.xs} 0;
`;

const SettingDescription = styled.p`
  color: ${theme.colors.textMuted};
  margin: 0;
  font-size: ${theme.typography.fontSizes.sm};
  line-height: 1.5;
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: ${theme.spacing.lg};
  animation: ${fadeIn} 0.3s ease-out;
`;

const ModalContent = styled.div`
  background: ${theme.colors.cardBackground};
  border: 1px solid ${theme.colors.cardBorder};
  border-radius: ${theme.borderRadius.large};
  padding: ${theme.spacing.xl};
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: ${theme.shadows.card};
  position: relative;

  @media (max-width: 480px) {
    padding: ${theme.spacing.lg};
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.xl};
  padding-bottom: ${theme.spacing.lg};
  border-bottom: 1px solid ${theme.colors.cardBorder};
`;

const ModalTitle = styled.h2`
  margin: 0;
  color: ${theme.colors.textPrimary};
  font-size: ${theme.typography.fontSizes.xl};
  background: ${theme.gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: ${theme.colors.textMuted};
  padding: 0;
  line-height: 1;
  transition: color 0.2s;
  
  &:hover {
    color: ${theme.colors.primary};
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
`;

const Label = styled.label`
  color: ${theme.colors.textPrimary};
  font-size: ${theme.typography.fontSizes.sm};
  font-weight: ${theme.typography.fontWeights.semibold};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
`;

const Input = styled.input`
  background: ${theme.colors.inputBackground};
  border: 2px solid ${theme.colors.inputBorder};
  color: ${theme.colors.textPrimary};
  padding: ${theme.spacing.md};
  border-radius: ${theme.borderRadius.medium};
  font-size: ${theme.typography.fontSizes.md};
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(255, 215, 0, 0.1);
  }

  &::placeholder {
    color: ${theme.colors.textMuted};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  margin-top: ${theme.spacing.lg};

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const Button = styled.button`
  flex: 1;
  padding: ${theme.spacing.md} ${theme.spacing.xl};
  background: ${props => props.$primary ? theme.gradients.primary : 'transparent'};
  color: ${props => props.$primary ? '#000' : theme.colors.textPrimary};
  border: 2px solid ${props => props.$primary ? 'transparent' : theme.colors.inputBorder};
  border-radius: ${theme.borderRadius.medium};
  font-weight: ${theme.typography.fontWeights.semibold};
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: ${theme.typography.fontSizes.md};

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.button};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const GuideContent = styled.div`
  color: ${theme.colors.textSecondary};
  line-height: 1.6;

  h3 {
    color: ${theme.colors.textPrimary};
    margin: ${theme.spacing.lg} 0 ${theme.spacing.md} 0;
    font-size: ${theme.typography.fontSizes.lg};
    display: flex;
    align-items: center;
    gap: ${theme.spacing.sm};
  }

  p {
    margin: ${theme.spacing.md} 0;
  }

  ul {
    margin: ${theme.spacing.md} 0;
    padding-left: ${theme.spacing.xl};
  }

  li {
    margin: ${theme.spacing.sm} 0;
  }

  strong {
    color: ${theme.colors.primary};
  }
`;

const ErrorMessage = styled.div`
  color: #ff4444;
  background: rgba(255, 68, 68, 0.1);
  border: 1px solid rgba(255, 68, 68, 0.3);
  padding: ${theme.spacing.md};
  border-radius: ${theme.borderRadius.medium};
  font-size: ${theme.typography.fontSizes.sm};
  margin-top: ${theme.spacing.sm};
`;

const SuccessMessage = styled.div`
  color: #4CAF50;
  background: rgba(76, 175, 80, 0.1);
  border: 1px solid rgba(76, 175, 80, 0.3);
  padding: ${theme.spacing.md};
  border-radius: ${theme.borderRadius.medium};
  font-size: ${theme.typography.fontSizes.sm};
  margin-top: ${theme.spacing.sm};
`;

const UserProfile = ({ onNavigate }) => {
  const { user } = useAuth();
  const [activeModal, setActiveModal] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const avatarInputRef = useRef(null);

  // Cosmetics state
  const [activeFrame, setActiveFrame] = useState(null);
  const [activeBadge, setActiveBadge] = useState({ imageUrl: null, name: null });
  const [cosmetics, setCosmetics] = useState({ frameColors: [], badges: [] });
  const [showCosmeticsPanel, setShowCosmeticsPanel] = useState(false);
  const [savingCosmetics, setSavingCosmetics] = useState(false);
  const [pendingFrame, setPendingFrame] = useState(null);
  const [pendingBadge, setPendingBadge] = useState(null);

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        username: user.username || ''
      }));
      setAvatarUrl(user.avatarUrl || null);
      // Init active cosmetics from user metadata
      const meta = user.metadata || {};
      setActiveFrame(meta.frameColor || null);
      setActiveBadge({ imageUrl: meta.badgeImageUrl || null, name: meta.badgeName || null });
      // Fetch available cosmetics
      ApiService.getCosmetics().then(res => {
        if (res?.data) {
          setCosmetics(res.data);
          // Sync active from server (source of truth)
          if (res.data.active) {
            setActiveFrame(res.data.active.frameColor || null);
            const savedBadge = res.data.active.badgeImageUrl
              ? { imageUrl: res.data.active.badgeImageUrl, name: res.data.active.badgeName || null }
              : null;
            // Auto-pick first trophy if no badge is explicitly saved
            if (savedBadge) {
              setActiveBadge(savedBadge);
            } else if (res.data.badges && res.data.badges.length > 0) {
              const first = res.data.badges.find(b => b.image);
              if (first) setActiveBadge({ imageUrl: first.image, name: first.name });
            }
          }
        }
      }).catch(() => {});
    }
  }, [user]);

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be under 5MB.');
      return;
    }
    setUploadingAvatar(true);
    try {
      const result = await ApiService.uploadAvatar(file);
      if (result?.avatarUrl) {
        setAvatarUrl(result.avatarUrl);
        const updatedUser = { ...user, avatarUrl: result.avatarUrl };
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }
    } catch (err) {
      alert('Failed to upload avatar. Please try again.');
    } finally {
      setUploadingAvatar(false);
      if (avatarInputRef.current) avatarInputRef.current.value = '';
    }
  };

  const openCosmeticsPanel = () => {
    setPendingFrame(activeFrame);
    setPendingBadge(activeBadge);
    setShowCosmeticsPanel(true);
  };

  // Lock body scroll while cosmetics modal is open
  useEffect(() => {
    if (showCosmeticsPanel) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [showCosmeticsPanel]);

  const handleSaveCosmetics = async () => {
    setSavingCosmetics(true);
    try {
      const result = await ApiService.updateCosmetics({
        frameColor: pendingFrame || null,
        badgeImageUrl: pendingBadge?.imageUrl || null,
        badgeName: pendingBadge?.name || null,
      });
      if (result?.success) {
        setActiveFrame(pendingFrame);
        setActiveBadge(pendingBadge || { imageUrl: null, name: null });
        const updatedUser = { ...user, metadata: result.metadata };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        setShowCosmeticsPanel(false);
      }
    } catch (err) {
      // silent
    } finally {
      setSavingCosmetics(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
    setSuccess('');
  };

  const handleEditProfile = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);

    try {
      const response = await ApiService.updateUser(user.id, {
        username: formData.username
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to update profile');
      }

      // Update localStorage with new user data
      const updatedUser = { ...user, ...response.user };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      setSuccess('Profile updated successfully!');
      setTimeout(() => {
        setActiveModal(null);
        window.location.reload(); // Reload to update user context
      }, 1500);
    } catch (err) {
      setError(err.message || 'Failed to update profile');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validation
    if (!formData.currentPassword || !formData.newPassword || !formData.confirmPassword) {
      setError('All fields are required');
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (formData.newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    setIsLoading(true);

    try {
      const response = await ApiService.changePassword(
        formData.currentPassword,
        formData.newPassword,
        formData.confirmPassword
      );

      if (!response.success) {
        throw new Error(response.message || 'Failed to change password');
      }

      setSuccess('Password changed successfully!');
      setFormData(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }));
      
      setTimeout(() => {
        setActiveModal(null);
      }, 1500);
    } catch (err) {
      setError(err.message || 'Failed to change password');
    } finally {
      setIsLoading(false);
    }
  };

  const closeModal = () => {
    setActiveModal(null);
    setError('');
    setSuccess('');
    setFormData(prev => ({
      ...prev,
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    }));
  };

  const getMemberSince = () => {
    if (user?.createdAt) {
      return new Date(user.createdAt).toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric'
      });
    }
    return 'Recently';
  };

  const getInitials = () => {
    if (user?.username) {
      return user.username.substring(0, 2).toUpperCase();
    }
    return '??';
  };

  return (
    <ProfileContainer>
      <PWAInstallBanner position="top" />
      <ContentWrapper>
        <ProfileHeader>
          <HeaderContent>
            <AvatarSection>
              <Avatar frameColor={activeFrame}>
                {avatarUrl
                  ? <AvatarImage
                      src={resolveAvatarUrl(avatarUrl)}
                      alt={user?.username}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.textContent = getInitials();
                      }}
                    />
                  : getInitials()
                }
              </Avatar>
              <AvatarOverlay
                onClick={() => avatarInputRef.current?.click()}
                title="Change profile picture"
                disabled={uploadingAvatar}
              >
                <FaCamera />
                {uploadingAvatar ? 'Uploading...' : 'Change'}
              </AvatarOverlay>
              <input
                ref={avatarInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleAvatarUpload}
              />
              <GamerBadge onClick={openCosmeticsPanel} title="Customize badge & frame" style={{ cursor: 'pointer' }}>
                {activeBadge.imageUrl
                  ? <img
                      src={resolveAvatarUrl(activeBadge.imageUrl)}
                      alt={activeBadge.name || 'Badge'}
                      style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '50%' }}
                      onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.innerHTML = ''; }}
                    />
                  : <FaGamepad />
                }
              </GamerBadge>
              <CustomizeBtn onClick={openCosmeticsPanel} title="Customize">
                <FaPalette />
              </CustomizeBtn>
            </AvatarSection>

            {showCosmeticsPanel && ReactDOM.createPortal(
              <CosmeticsOverlay onClick={() => setShowCosmeticsPanel(false)}>
                <CosmeticsModal onClick={e => e.stopPropagation()}>
                  <CosmeticsHeader>
                    <CosmeticsTitle>Customize Profile</CosmeticsTitle>
                    <CosmeticsSubtitle>Unlocked from QR scans</CosmeticsSubtitle>
                  </CosmeticsHeader>

                  <PreviewArea>
                    <PreviewAvatar frameColor={pendingFrame}>
                      {avatarUrl
                        ? <PreviewAvatarImg
                            src={resolveAvatarUrl(avatarUrl)}
                            alt="preview"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.parentElement.innerHTML = `<span>${getInitials()}</span>`;
                            }}
                          />
                        : <span>{getInitials()}</span>
                      }
                      <PreviewBadge>
                        {pendingBadge?.imageUrl
                          ? <img
                              src={resolveAvatarUrl(pendingBadge.imageUrl)}
                              alt={pendingBadge.name || 'Badge'}
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          : <FaGamepad style={{ color: '#000', fontSize: '1rem' }} />
                        }
                      </PreviewBadge>
                    </PreviewAvatar>
                  </PreviewArea>

                  <CosmeticsBody>
                    <SectionLabel>Frame Color</SectionLabel>
                    <SwatchRow>
                      <NoneBtn active={!pendingFrame} onClick={() => setPendingFrame(null)}>Default</NoneBtn>
                      {(cosmetics.frameColors || []).map(color => (
                        <Swatch
                          key={color}
                          color={color}
                          active={pendingFrame === color}
                          onClick={() => setPendingFrame(color)}
                        />
                      ))}
                      {(!cosmetics.frameColors || cosmetics.frameColors.length === 0) && (
                        <span style={{ color: theme.colors.textMuted, fontSize: '0.78rem' }}>Scan QR codes to unlock colors</span>
                      )}
                    </SwatchRow>

                    <SectionLabel>Trophy Badge</SectionLabel>
                    <BadgeGrid>
                      <div>
                        <BadgeOption
                          active={!pendingBadge?.imageUrl}
                          onClick={() => setPendingBadge(null)}
                          title="Default"
                        >
                          <FaGamepad style={{ color: theme.colors.primary, fontSize: '1.6rem', margin: 'auto', display: 'block' }} />
                        </BadgeOption>
                        <BadgeOptionLabel>Default</BadgeOptionLabel>
                      </div>
                      {(cosmetics.badges || []).filter(b => b.image).map(badge => (
                        <div key={badge.id}>
                          <BadgeOption
                            active={pendingBadge?.imageUrl === badge.image}
                            onClick={() => setPendingBadge({ imageUrl: badge.image, name: badge.name })}
                            title={badge.name}
                          >
                            <img src={badge.image} alt={badge.name} />
                          </BadgeOption>
                          <BadgeOptionLabel>{badge.name}</BadgeOptionLabel>
                        </div>
                      ))}
                      {(!cosmetics.badges || cosmetics.badges.filter(b => b.image).length === 0) && (
                        <span style={{ color: theme.colors.textMuted, fontSize: '0.78rem', gridColumn: '2 / -1', alignSelf: 'center' }}>Scan QR codes to unlock trophies</span>
                      )}
                    </BadgeGrid>
                  </CosmeticsBody>

                  <CosmeticsFooter>
                    <CancelBtn onClick={() => setShowCosmeticsPanel(false)}>Cancel</CancelBtn>
                    <ApplyBtn onClick={handleSaveCosmetics} disabled={savingCosmetics}>
                      <FaCheck /> {savingCosmetics ? 'Saving...' : 'Apply'}
                    </ApplyBtn>
                  </CosmeticsFooter>
                </CosmeticsModal>
              </CosmeticsOverlay>,
              document.body
            )}
            <ProfileInfo>
              <ProfileName>
                {user?.username || 'Player'}
              </ProfileName>
              <ProfileEmail>
                {user?.email || 'No email'}
              </ProfileEmail>
              <ProfileStats>
                <StatItem>
                  <FaTrophy />
                  <span>{user?.points?.toLocaleString() || 0} <img src='images/ggcoin.svg' alt='GG Coin' style={{ width: '16px', height: '16px', verticalAlign: 'text-bottom' }} /></span>
                </StatItem>
                <StatItem>
                  <FaCalendar />
                  <span>Member Since {getMemberSince()}</span>
                </StatItem>
                <StatItem>
                  <FaStar />
                  <span>Level {Math.floor((user?.points || 0) / 1000) + 1}</span>
                </StatItem>
              </ProfileStats>
            </ProfileInfo>
          </HeaderContent>
        </ProfileHeader>

        <SettingsSection>
          <SectionTitle>
            <FaCog /> Settings
          </SectionTitle>
          <SettingsGrid>
            <SettingCard onClick={() => setActiveModal('edit-profile')}>
              <SettingIcon>
                <FaEdit />
              </SettingIcon>
              <SettingTitle>Edit Profile</SettingTitle>
              <SettingDescription>
                Update your username and profile information
              </SettingDescription>
            </SettingCard>

            <SettingCard onClick={() => setActiveModal('change-password')}>
              <SettingIcon>
                <FaKey />
              </SettingIcon>
              <SettingTitle>Change Password</SettingTitle>
              <SettingDescription>
                Update your account password for security
              </SettingDescription>
            </SettingCard>

            <SettingCard onClick={() => setActiveModal('app-guide')}>
              <SettingIcon>
                <FaBook />
              </SettingIcon>
              <SettingTitle>App Guide</SettingTitle>
              <SettingDescription>
                Learn how to use GG Trophy effectively
              </SettingDescription>
            </SettingCard>

            <SettingCard onClick={() => setActiveModal('pwa-install')}>
              <SettingIcon>
                <FaMobileAlt />
              </SettingIcon>
              <SettingTitle>Install App</SettingTitle>
              <SettingDescription>
                Install GG Trophy on your device for quick access
              </SettingDescription>
            </SettingCard>
          </SettingsGrid>
        </SettingsSection>
      </ContentWrapper>

      {/* Edit Profile Modal */}
      {activeModal === 'edit-profile' && (
        <Modal onClick={closeModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                <FaEdit /> Edit Profile
              </ModalTitle>
              <CloseButton onClick={closeModal}>
                <FaTimes />
              </CloseButton>
            </ModalHeader>
            <Form onSubmit={handleEditProfile}>
              <FormGroup>
                <Label>
                  <FaUser /> Username
                </Label>
                <Input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  placeholder="Enter your username"
                  required
                />
              </FormGroup>
              {error && <ErrorMessage>{error}</ErrorMessage>}
              {success && <SuccessMessage>{success}</SuccessMessage>}
              <ButtonGroup>
                <Button type="button" onClick={closeModal}>
                  <FaTimes /> Cancel
                </Button>
                <Button type="submit" $primary disabled={isLoading}>
                  <FaSave /> {isLoading ? 'Saving...' : 'Save Changes'}
                </Button>
              </ButtonGroup>
            </Form>
          </ModalContent>
        </Modal>
      )}

      {/* Change Password Modal */}
      {activeModal === 'change-password' && (
        <Modal onClick={closeModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                <FaKey /> Change Password
              </ModalTitle>
              <CloseButton onClick={closeModal}>
                <FaTimes />
              </CloseButton>
            </ModalHeader>
            <Form onSubmit={handleChangePassword}>
              <FormGroup>
                <Label>
                  <FaShieldAlt /> Current Password
                </Label>
                <Input
                  type="password"
                  name="currentPassword"
                  value={formData.currentPassword}
                  onChange={handleInputChange}
                  placeholder="Enter current password"
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>
                  <FaKey /> New Password
                </Label>
                <Input
                  type="password"
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleInputChange}
                  placeholder="Enter new password"
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>
                  <FaKey /> Confirm New Password
                </Label>
                <Input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  placeholder="Confirm new password"
                  required
                />
              </FormGroup>
              {error && <ErrorMessage>{error}</ErrorMessage>}
              {success && <SuccessMessage>{success}</SuccessMessage>}
              <ButtonGroup>
                <Button type="button" onClick={closeModal}>
                  <FaTimes /> Cancel
                </Button>
                <Button type="submit" $primary disabled={isLoading}>
                  <FaSave /> {isLoading ? 'Changing...' : 'Change Password'}
                </Button>
              </ButtonGroup>
            </Form>
          </ModalContent>
        </Modal>
      )}

      {/* App Guide Modal */}
      {activeModal === 'app-guide' && (
        <Modal onClick={closeModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                <FaBook /> App Guide
              </ModalTitle>
              <CloseButton onClick={closeModal}>
                <FaTimes />
              </CloseButton>
            </ModalHeader>
            <GuideContent>
              <h3><FaTrophy /> Welcome to GG Trophy!</h3>
              <p>
                GG Trophy is your ultimate gaming rewards platform. Here's how to make the most of it:
              </p>

              <h3><FaQrcode /> Scanning QR Codes</h3>
              <ul>
                <li>Navigate to the <strong>Scan</strong> page from the menu</li>
                <li>Point your camera at a GG Trophy QR code</li>
                <li>Earn rewards instantly when the code is scanned</li>
                <li>Each code can only be scanned once per day</li>
              </ul>

              <h3><FaTrophy /> Earning Rewards</h3>
              <ul>
                <li>Scan QR codes to earn rewards</li>
                <li>Spin the wheel for bonus rewards</li>
                <li>Complete daily challenges (coming soon)</li>
                <li>Participate in community events</li>
              </ul>

              <h3><FaGift /> Redeeming Rewards</h3>
              <ul>
                <li>Browse the <strong>Catalog</strong> for available rewards</li>
                <li>Choose gift cards, game items, or special offers</li>
                <li>Use your balance to redeem items</li>
                <li>Check your <strong>Inventory</strong> for redeemed items</li>
              </ul>

              <h3><FaUsers /> Community Features</h3>
              <ul>
                <li>Join the <strong>Chat</strong> to connect with other players</li>
                <li>Read the latest <strong>News</strong> and announcements</li>
                <li>Share tips and strategies with the community</li>
              </ul>

              <h3><FaCog /> Account Security</h3>
              <ul>
                <li>Change your password regularly</li>
                <li>Keep your account information up to date</li>
                <li>Never share your login credentials</li>
              </ul>

              <p>
                Need more help? Contact support or visit our FAQ section.
              </p>
            </GuideContent>
            <ButtonGroup>
              <Button $primary onClick={closeModal}>
                Got It!
              </Button>
            </ButtonGroup>
          </ModalContent>
        </Modal>
      )}

      {/* PWA Install Guide Modal */}
      {activeModal === 'pwa-install' && ((
        <Modal onClick={closeModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                <FaHome /> Install GG Trophy App
              </ModalTitle>
              <CloseButton onClick={closeModal}>
                <FaTimes />
              </CloseButton>
            </ModalHeader>
            <GuideContent>
              <h3><FaMobileAlt /> Why Install as an App?</h3>
              <ul>
                <li><strong>Faster Access:</strong> Launch directly from your home screen</li>
                <li><strong>Offline Support:</strong> Works even without internet</li>
                <li><strong>Native Feel:</strong> Full-screen experience without browser UI</li>
                <li><strong>Instant Updates:</strong> Always get the latest features</li>
                <li><strong>Less Storage:</strong> Much smaller than native apps</li>
              </ul>

              <h3><FaApple /> iOS Installation (iPhone/iPad)</h3>
              <ul>
                <li>Open GG Trophy in <strong>Safari</strong> browser</li>
                <li>Tap the <strong>Share</strong> button <FaShareAlt style={{display: 'inline', fontSize: '0.9em'}} /> at the bottom</li>
                <li>Scroll down and tap <strong>"Add to Home Screen"</strong> <FaPlus style={{display: 'inline', fontSize: '0.9em'}} /></li>
                <li>Tap <strong>"Add"</strong> in the top right corner</li>
                <li>The GG Trophy icon will appear on your home screen!</li>
              </ul>
              <p style={{fontSize: '0.9em', color: theme.colors.textMuted, marginTop: '0.5rem'}}>
                <strong>Note:</strong> Installation only works in Safari on iOS, not Chrome or other browsers.
              </p>

              <h3><FaAndroid /> Android Installation</h3>
              <p><strong>Option 1: Automatic Prompt</strong></p>
              <ul>
                <li>Chrome will show an "Install" banner when you visit the app</li>
                <li>Tap <strong>"Install"</strong> to add to your home screen</li>
              </ul>
              <p><strong>Option 2: Manual Installation</strong></p>
              <ul>
                <li>Open GG Trophy in <strong>Chrome</strong> browser</li>
                <li>Tap the <strong>Menu</strong> button <FaEllipsisV style={{display: 'inline', fontSize: '0.9em'}} /> (three dots)</li>
                <li>Select <strong>"Add to Home screen"</strong> or <strong>"Install app"</strong></li>
                <li>Confirm by tapping <strong>"Install"</strong></li>
                <li>Launch from your home screen!</li>
              </ul>

              <h3><FaMobileAlt /> Desktop Installation (Chrome/Edge)</h3>
              <ul>
                <li>Look for the <strong>Install</strong> icon <FaPlus style={{display: 'inline', fontSize: '0.9em'}} /> in the address bar</li>
                <li>Click the install icon or button</li>
                <li>Click <strong>"Install"</strong> in the confirmation dialog</li>
                <li>GG Trophy will open as a standalone app</li>
              </ul>

              <h3><FaTrophy /> After Installation</h3>
              <p>
                Once installed, you can launch GG Trophy like any other app on your device.
                You'll get a full-screen experience with faster loading and offline support!
              </p>
            </GuideContent>
            <ButtonGroup>
              <Button $primary onClick={closeModal}>
                Got It!
              </Button>
            </ButtonGroup>
          </ModalContent>
        </Modal>
      ))}
    </ProfileContainer>
  );
};

export default UserProfile;
